import { create } from "zustand";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
}

interface CartState {
  items: CartItem[];
  total: number;
  addToCart: (item: Omit<CartItem, "quantity">) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
}

const getInitialCart = (): CartItem[] => {
  try {
    return JSON.parse(localStorage.getItem("cart") || "[]");
  } catch {
    return [];
  }
};

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
};

const saveCart = (items: CartItem[]) => {
  localStorage.setItem("cart", JSON.stringify(items));
};

export const useCartStore = create<CartState>((set) => {
  const initialItems = getInitialCart();
  return {
    items: initialItems,
    total: calculateTotal(initialItems),
    addToCart: (item) =>
      set((state) => {
        const existingItem = state.items.find((i) => i.id === item.id);
        let newItems: CartItem[];

        if (existingItem) {
          newItems = state.items.map((i) =>
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
          );
        } else {
          newItems = [...state.items, { ...item, quantity: 1 }];
        }

        saveCart(newItems);
        return { items: newItems, total: calculateTotal(newItems) };
      }),
    removeFromCart: (id) =>
      set((state) => {
        const newItems = state.items.filter((item) => item.id !== id);
        saveCart(newItems);
        return { items: newItems, total: calculateTotal(newItems) };
      }),
    updateQuantity: (id, quantity) =>
      set((state) => {
        let newItems: CartItem[];
        if (quantity <= 0) {
          newItems = state.items.filter((item) => item.id !== id);
        } else {
          newItems = state.items.map((item) =>
            item.id === id ? { ...item, quantity } : item
          );
        }
        saveCart(newItems);
        return { items: newItems, total: calculateTotal(newItems) };
      }),
    clearCart: () => {
      localStorage.removeItem("cart");
      set({ items: [], total: 0 });
    },
  };
});

